import { Vector3 } from 'three';

import { Bone, Skeleton } from '../../skeleton/core';
import {
  ROLE_TERMS,
  isInlineChestLikeName,
  isPrimarySpineBridgeName,
  isSimpleHeadSegmentName,
  isSpineLikeName,
  isTorsoAuxiliaryLeafName,
} from '../../schema/mapping';
import { centerAlignmentScore } from '../scoring';
import type { SkeletonSpatialData, SpatialLandmarks, BranchingPoint } from '../spatial';
import { findUpwardCenteredChain } from '../spatial';
import type { RecognizerConfig } from '../config';

// ─── Position helpers ────────────────────────────────────────────────────

function boneWorldPos(bone: Bone): Vector3 {
  const e = bone.restWorld.elements;
  return new Vector3(e[12], e[13], e[14]);
}

function getTraceableChildren(
  skel: Skeleton,
  boneIdx: number,
  options?: {
    includeAuxiliary?: (bone: Bone) => boolean;
    skip?: (bone: Bone) => boolean;
  },
): number[] {
  const traceableChildren: number[] = [];
  const visited = new Set<number>();

  const visit = (childIdx: number): void => {
    if (visited.has(childIdx)) return;
    visited.add(childIdx);

    const child = skel.bone(childIdx);
    const shouldSkip = options?.skip?.(child) ?? false;
    if (shouldSkip && (!child.auxiliary || child.children.length === 0)) return;

    if (!child.auxiliary) {
      traceableChildren.push(childIdx);
      return;
    }

    if (options?.includeAuxiliary?.(child)) {
      traceableChildren.push(childIdx);
      return;
    }

    for (const grandchildIdx of child.children) {
      visit(grandchildIdx);
    }
  };

  for (const childIdx of skel.bone(boneIdx).children) {
    visit(childIdx);
  }

  return traceableChildren;
}

function hasClavicleOrNeckChild(skel: Skeleton, boneIdx: number): boolean {
  const childTerms = [
    ...(ROLE_TERMS['Clavicle'] ?? []),
    ...(ROLE_TERMS['Neck'] ?? []),
  ];
  return getTraceableChildren(skel, boneIdx, {
    includeAuxiliary: (child) => {
      const childName = child.name.toLowerCase();
      return childTerms.some((term) => childName.includes(term));
    },
  }).some((childIdx) => {
    const child = skel.bone(childIdx);
    const childName = child.name.toLowerCase();
    return childTerms.some((term) => childName.includes(term));
  });
}

function torsoContinuationScore(
  skel: Skeleton,
  boneIdx: number,
  latAxis: Vector3,
  pelvisPos: Vector3,
  centerTolerance: number,
): number {
  const bone = skel.bone(boneIdx);
  const name = bone.name.toLowerCase();
  let score = centerAlignmentScore(
    boneWorldPos(bone),
    latAxis,
    pelvisPos,
    centerTolerance,
  );

  if (isSpineLikeName(name)) score += 2.0;
  if ((ROLE_TERMS['Chest'] ?? []).some((term) => name.includes(term))) score += 1.5;
  if (hasClavicleOrNeckChild(skel, boneIdx)) score += 3.0;
  if (isTorsoAuxiliaryLeafName(name)) score -= 4.0;
  if (bone.children.length === 0) score -= 1.0;

  return score;
}

// ─── Spatial chest candidates ────────────────────────────────────────────

function findSpatialChestCandidates(
  skel: Skeleton,
  spatialData: SkeletonSpatialData,
  pelvisVertical: number,
): number[] {
  const candidates: [number, number][] = [];

  for (const [boneIdx, bp] of spatialData.branchingPoints) {
    const bone = skel.bone(boneIdx);

    if (bone.hasSide) continue;
    if (bp.verticalPosition <= pelvisVertical + 0.05) continue;
    if (!bp.hasSymmetricChildren) continue;
    if (!bp.hasLateralChildren) continue;
    if (bp.verticalPosition > 0.85) continue;
    if (Math.abs(bp.lateralOffset) > 0.15) continue;

    let score = 0.0;
    score += bp.symmetryScore * 2.0;
    if (bp.hasLateralChildren) score += 1.0;
    if (bp.hasUpwardChildren) score += 0.5;
    if (bp.verticalPosition >= 0.45 && bp.verticalPosition <= 0.80) score += 0.5;

    candidates.push([score, boneIdx]);
  }

  candidates.sort((a, b) => b[0] - a[0]);
  return candidates.map(([, idx]) => idx);
}

// ─── Spatial spine chain ─────────────────────────────────────────────────

function findSpatialSpineChain(
  skel: Skeleton,
  pelvisIdx: number,
  spatialData: SkeletonSpatialData,
  landmarks?: SpatialLandmarks | null,
): [number[], number | null] {
  const pelvis = skel.bone(pelvisIdx);
  const pelvisVert = spatialData.getVerticalPosition(pelvisIdx);
  const pelvisRawX = pelvis.restWorld.elements[12];
  const centerThreshold = spatialData.totalHeight * 0.05;

  function isCentered(boneIdx: number): [boolean, number] {
    const bone = skel.bone(boneIdx);
    const rawX = bone.restWorld.elements[12];
    const xOffset = Math.abs(rawX - pelvisRawX);
    const isNearCenter = xOffset <= centerThreshold;
    const centeringScore = centerThreshold > 0
      ? 1.0 - (xOffset / centerThreshold)
      : 1.0;
    return [isNearCenter, Math.max(0, centeringScore)];
  }

  // Strategy 1: upward-centered children from pelvis
  let bestSpineStart: number | null = null;
  let bestScore = -1.0;

  for (const childIdx of pelvis.children) {
    const child = skel.bone(childIdx);
    if (child.auxiliary) continue;

    const childVert = spatialData.getVerticalPosition(childIdx);
    if (childVert <= pelvisVert) continue;

    const [isNear, centeringScore] = isCentered(childIdx);
    if (!isNear) continue;

    const score = (childVert - pelvisVert) + centeringScore;
    if (score > bestScore) {
      bestScore = score;
      bestSpineStart = childIdx;
    }
  }

  // Strategy 2: siblings of pelvis
  if (bestSpineStart == null && pelvis.parent !== null && pelvis.parent >= 0) {
    const parent = skel.bone(pelvis.parent);
    for (const sibIdx of parent.children) {
      if (sibIdx === pelvisIdx) continue;
      const sib = skel.bone(sibIdx);
      if (sib.auxiliary) continue;

      const sibVert = spatialData.getVerticalPosition(sibIdx);
      if (sibVert <= pelvisVert) continue;

      const [isNear, centeringScore] = isCentered(sibIdx);
      if (!isNear) continue;

      const score = (sibVert - pelvisVert) + centeringScore;
      if (score > bestScore) {
        bestScore = score;
        bestSpineStart = sibIdx;
      }
    }
  }

  // Strategy 3: auxiliary bones that are upward and centered
  if (bestSpineStart == null) {
    for (const childIdx of pelvis.children) {
      const child = skel.bone(childIdx);
      if (!child.auxiliary) continue;

      const childVert = spatialData.getVerticalPosition(childIdx);
      if (childVert <= pelvisVert) continue;

      const [isNear, centeringScore] = isCentered(childIdx);
      if (!isNear) continue;

      const score = (childVert - pelvisVert) + centeringScore;
      if (score > bestScore) {
        bestScore = score;
        bestSpineStart = childIdx;
        child.auxiliary = false;
      }
    }
  }

  // Strategy 4: trace backwards from chest landmark
  if (bestSpineStart == null && landmarks != null && landmarks.likelyChest != null) {
    const chestIdx = landmarks.likelyChest;
    const spineChain = traceSpineFromChest(skel, chestIdx, pelvisVert, spatialData);
    if (spineChain.length > 0) return [spineChain, chestIdx];
  }

  if (bestSpineStart == null) return [[], null];

  let spineChain = findUpwardCenteredChain(skel, bestSpineStart, spatialData, {
    stopAtBranching: true,
    useRawXCentering: true,
  });

  let chestIdx: number | null = null;
  if (spineChain.length > 0) {
    const lastBone = spineChain[spineChain.length - 1];
    const bp = spatialData.getBranchingPoint(lastBone);
    if (bp && bp.hasLateralChildren) {
      chestIdx = lastBone;
    } else {
      const lastBoneObj = skel.bone(lastBone);
      for (const childIdx of lastBoneObj.children) {
        if (skel.bone(childIdx).auxiliary) continue;
        const cbp = spatialData.getBranchingPoint(childIdx);
        if (cbp && cbp.hasLateralChildren) {
          chestIdx = childIdx;
          spineChain.push(chestIdx);
          break;
        }
      }
    }
  }

  // Promote chest to the HIGHEST branching point with lateral children
  if (chestIdx != null) {
    const startRawX = skel.bone(bestSpineStart).restWorld.elements[12];
    const chestCenterThreshold = spatialData.totalHeight * 0.05;

    while (true) {
      const chestBone = skel.bone(chestIdx!);
      const chestVert = spatialData.getVerticalPosition(chestIdx!);
      let promoted = false;

      for (const childIdx of chestBone.children) {
        if (skel.bone(childIdx).auxiliary) continue;
        const childVert = spatialData.getVerticalPosition(childIdx);
        if (childVert <= chestVert) continue;

        const childRawX = skel.bone(childIdx).restWorld.elements[12];
        if (Math.abs(childRawX - startRawX) > chestCenterThreshold) continue;

        const childBp = spatialData.getBranchingPoint(childIdx);
        if (childBp && childBp.hasLateralChildren) {
          const childNonAux = skel.bone(childIdx).children.filter(
            c => !skel.bone(c).auxiliary,
          );
          if (childNonAux.length < 4) continue;

          spineChain.push(childIdx);
          chestIdx = childIdx;
          promoted = true;
          break;
        }
      }

      if (!promoted) break;
    }
  }

  // Extend chain using landmark chest if higher
  if (landmarks != null && landmarks.likelyChest != null) {
    const landmarkChest = landmarks.likelyChest;
    const landmarkVert = spatialData.getVerticalPosition(landmarkChest);

    if (spineChain.length > 0) {
      const lastVert = spatialData.getVerticalPosition(spineChain[spineChain.length - 1]);
      if (landmarkVert > lastVert) {
        chestIdx = landmarkChest;
        const chestTrace = traceSpineFromChest(
          skel, landmarkChest, lastVert, spatialData, spineChain,
        );
        if (chestTrace.length > 0) {
          const currentSet = new Set(spineChain);
          const extension = chestTrace.filter(b => !currentSet.has(b));
          if (extension.length > 0) spineChain = [...spineChain, ...extension];
        }
      }
    } else {
      const chestSpine = traceSpineFromChest(skel, landmarkChest, pelvisVert, spatialData);
      if (chestSpine.length > 0) return [chestSpine, landmarkChest];
    }
  }

  return [spineChain, chestIdx];
}

// ─── Trace spine from chest ──────────────────────────────────────────────

function traceSpineFromChest(
  skel: Skeleton,
  chestIdx: number,
  pelvisVert: number,
  spatialData: SkeletonSpatialData,
  existingChain?: number[],
): number[] {
  const spineChain: number[] = [];
  let currentIdx: number | null = chestIdx;
  const existingSet = existingChain ? new Set(existingChain) : new Set<number>();

  while (currentIdx != null) {
    const bone = skel.bone(currentIdx);

    if (existingSet.has(currentIdx)) break;

    const lat = Math.abs(spatialData.getLateralOffset(currentIdx));
    if (lat > 0.30) break;

    const vert = spatialData.getVerticalPosition(currentIdx);
    if (!existingChain && vert <= pelvisVert + 0.05) break;

    spineChain.push(currentIdx);

    if (bone.parent == null || bone.parent < 0) break;

    const parent = skel.bone(bone.parent);
    if (parent.auxiliary) {
      currentIdx = parent.parent != null ? parent.parent : null;
    } else {
      currentIdx = bone.parent;
    }
  }

  spineChain.reverse();
  return spineChain;
}

// ─── Spatial neck/head ───────────────────────────────────────────────────

function findSpatialNeckHead(
  skel: Skeleton,
  chestIdx: number,
  spatialData: SkeletonSpatialData,
): [number[], number | null] {
  const chest = skel.bone(chestIdx);
  const chestVert = spatialData.getVerticalPosition(chestIdx);
  const chestRawX = chest.restWorld.elements[12];
  const centerThreshold = spatialData.totalHeight * 0.05;

  let bestNeckStart: number | null = null;
  let bestScore = -1.0;

  for (const childIdx of getTraceableChildren(skel, chestIdx)) {
    const child = skel.bone(childIdx);
    const childVert = spatialData.getVerticalPosition(childIdx);
    const childRawX = child.restWorld.elements[12];

    if (childVert <= chestVert) continue;

    const xOffset = Math.abs(childRawX - chestRawX);
    if (xOffset > centerThreshold) continue;

    const centeringScore = centerThreshold > 0 ? 1.0 - xOffset / centerThreshold : 1.0;
    const score = (childVert - chestVert) + centeringScore;
    if (score > bestScore) {
      bestScore = score;
      bestNeckStart = childIdx;
    }
  }

  if (bestNeckStart == null) return [[], null];

  let neckChain = findUpwardCenteredChain(skel, bestNeckStart, spatialData, {
    stopAtBranching: false,
    stopAtHighBranching: true,
    useRawXCentering: true,
  });

  // Guard 1: prune bones whose direction points back to origin
  const upAxis = spatialData.upAxis;
  for (let i = 0; i < neckChain.length; i++) {
    const boneDir = skel.bone(neckChain[i]).direction;
    const dirLen = boneDir.length();
    if (dirLen > 1e-8) {
      const alignment = boneDir.clone().divideScalar(dirLen).dot(upAxis);
      if (alignment < -0.3) {
        neckChain = neckChain.slice(0, i);
        break;
      }
    }
  }

  if (neckChain.length === 0) return [[], null];

  // Sanity: very long first bone is head, not neck
  const skelHeight = spatialData.totalHeight > 1e-6 ? spatialData.totalHeight : 100.0;
  const firstNeckLen = skel.bone(neckChain[0]).length;
  if (firstNeckLen > skelHeight * 0.20) {
    return [[], neckChain[0]];
  }

  let headIdx: number | null = null;
  if (neckChain.length > 0) {
    const lastNeck = skel.bone(neckChain[neckChain.length - 1]);
    const lastNeckRawX = lastNeck.restWorld.elements[12];
    const nonAuxChildren = getTraceableChildren(skel, lastNeck.index, {
      skip: (child) => isTorsoAuxiliaryLeafName(child.name),
    });

    if (nonAuxChildren.length === 0) {
      if (neckChain.length > 1) {
        headIdx = neckChain[neckChain.length - 1];
        neckChain = neckChain.slice(0, -1);
      }
    } else {
      // Guard 3: skip candidates whose direction points strongly back to origin
      for (const childIdx of nonAuxChildren) {
        const childDir = skel.bone(childIdx).direction;
        const childDirLen = childDir.length();
        if (childDirLen > 1e-8) {
          const childAlign = childDir.clone().divideScalar(childDirLen).dot(upAxis);
          if (childAlign < -0.5) continue;
        }
        const childRawX = skel.bone(childIdx).restWorld.elements[12];
        const xOffset = Math.abs(childRawX - lastNeckRawX);
        if (xOffset < centerThreshold) {
          headIdx = childIdx;
          break;
        }
      }

      if (headIdx == null) {
        for (const childIdx of nonAuxChildren) {
          const childDir = skel.bone(childIdx).direction;
          const childDirLen = childDir.length();
          if (childDirLen > 1e-8) {
            const childAlign = childDir.clone().divideScalar(childDirLen).dot(upAxis);
            if (childAlign < -0.5) continue;
          }
          headIdx = childIdx;
          break;
        }
        if (headIdx == null) headIdx = nonAuxChildren[0];
      }
    }
  }

  return [neckChain, headIdx];
}

// ═════════════════════════════════════════════════════════════════════════
// MAIN DETECTION
// ═════════════════════════════════════════════════════════════════════════

export function detectSpineNeckHead(
  skel: Skeleton,
  pelvis: Bone,
  leftChain: number[],
  rightChain: number[],
  upAxis: Vector3,
  config: RecognizerConfig,
  lateralAxis?: Vector3 | null,
  spatialData?: SkeletonSpatialData | null,
  landmarks?: SpatialLandmarks | null,
  useSpatialFallback = false,
): [number[], number[], number | null, number | null] {
  // SPATIAL FALLBACK
  if (useSpatialFallback && spatialData != null) {
    const [spatialSpine, spatialChest] = findSpatialSpineChain(
      skel, pelvis.index, spatialData, landmarks,
    );

    if (spatialSpine.length > 0) {
      let neckChain: number[] = [];
      let headIdx: number | null = null;

      if (spatialChest != null) {
        [neckChain, headIdx] = findSpatialNeckHead(skel, spatialChest, spatialData);
      } else {
        [neckChain, headIdx] = findSpatialNeckHead(
          skel, spatialSpine[spatialSpine.length - 1], spatialData,
        );
      }

      if (neckChain.length === 0 && headIdx == null) {
        for (let si = spatialSpine.length - 1; si >= 0; si--) {
          const spineIdx = spatialSpine[si];
          if (spineIdx === spatialChest) continue;
          const bp = spatialData.getBranchingPoint(spineIdx);
          if (bp != null && bp.hasUpwardChildren) {
            [neckChain, headIdx] = findSpatialNeckHead(skel, spineIdx, spatialData);
            if (neckChain.length > 0 || headIdx != null) break;
          }
        }
      }

      return [spatialSpine, neckChain, headIdx, spatialChest];
    }
  }

  // NAMING-BASED DETECTION
  const pelvisPos = boneWorldPos(pelvis);
  const latAxis = lateralAxis ?? new Vector3(1, 0, 0);

  const allPositions = skel.bones.map(b => boneWorldPos(b));
  let centerTolerance = 0.5;
  if (allPositions.length > 0) {
    const lateralProjections = allPositions.map(p => p.dot(latAxis));
    const lateralExtent = Math.max(...lateralProjections) - Math.min(...lateralProjections);
    centerTolerance = Math.max(0.05 * lateralExtent, 0.5);
  }

  const effUpAxis = upAxis ?? new Vector3(0, 1, 0);

  const neckTerms = ROLE_TERMS['Neck'] ?? [];
  const headTerms = ROLE_TERMS['Head'] ?? [];

  const actualLegBones = new Set<number>();
  for (const chain of [leftChain, rightChain]) {
    if (chain) {
      for (const boneIdx of chain) {
        const boneName = skel.bone(boneIdx).name.toLowerCase();
        if (['thigh', 'calf', 'knee', 'ankle', 'foot', 'toe', 'femur', 'tibia', '足', 'ひざ'].some(
          tok => boneName.includes(tok),
        )) {
          actualLegBones.add(boneIdx);
        }
      }
    }
  }

  const upwardCandidates: number[] = [];

  function hasLegChildren(boneIdx: number): boolean {
    const bone = skel.bone(boneIdx);
    return bone.children.some(c => actualLegBones.has(c));
  }

  for (const child of pelvis.children) {
    if (actualLegBones.has(child)) continue;
    const childBone = skel.bone(child);
    if (childBone.auxiliary) continue;
    const childPos = boneWorldPos(childBone);
    const childName = childBone.name.toLowerCase();

    const goesUpward = childPos.clone().sub(pelvisPos).dot(effUpAxis) > 0;
    const spineToks = [
      ...(ROLE_TERMS['Spine'] ?? []),
      ...(ROLE_TERMS['Chest'] ?? []),
      'torso', 'body',
    ];
    const isSpineLike = spineToks.some(tok => childName.includes(tok));

    if (goesUpward || isSpineLike) {
      if (hasLegChildren(child)) {
        for (const grandchild of childBone.children) {
          if (actualLegBones.has(grandchild)) continue;
          const gcBone = skel.bone(grandchild);
          if (gcBone.auxiliary) continue;
          const gcName = gcBone.name.toLowerCase();
          const gcPos = boneWorldPos(gcBone);
          const gcGoesUp = gcPos.clone().sub(pelvisPos).dot(effUpAxis) > 0;
          const gcSpineToks = [
            ...(ROLE_TERMS['Spine'] ?? []),
            ...(ROLE_TERMS['Chest'] ?? []),
            ...(ROLE_TERMS['Neck'] ?? []),
            'torso', 'body',
          ];
          const gcIsSpine = gcSpineToks.some(tok => gcName.includes(tok));
          if (gcGoesUp || gcIsSpine) upwardCandidates.push(grandchild);
        }
      } else {
        upwardCandidates.push(child);
      }
    }
  }

  // Also check siblings
  if (pelvis.parent !== null && pelvis.parent >= 0) {
    const parent = skel.bone(pelvis.parent);
    const parentNameLower = parent.name.toLowerCase();
    const parentIsTorso =
      !parent.auxiliary &&
      isPrimarySpineBridgeName(parentNameLower);
    if (parentIsTorso && !upwardCandidates.includes(pelvis.parent)) {
      upwardCandidates.push(pelvis.parent);
    }

    for (const sib of parent.children) {
      if (sib === pelvis.index) continue;
      if (actualLegBones.has(sib)) continue;
      const sibBone = skel.bone(sib);
      if (sibBone.auxiliary) continue;

      const sibPos = boneWorldPos(sibBone);
      const verticalOffset = sibPos.clone().sub(pelvisPos).dot(effUpAxis);
      const nameLower = sibBone.name.toLowerCase();
      const sibSpineToks = [
        ...(ROLE_TERMS['Spine'] ?? []),
        ...(ROLE_TERMS['Chest'] ?? []),
        'torso', 'body',
      ];
      const isSpineLike = sibSpineToks.some(tok => nameLower.includes(tok));
      const nhToks = [...(ROLE_TERMS['Neck'] ?? []), ...(ROLE_TERMS['Head'] ?? [])];
      const isNeckHead = nhToks.some(tok => nameLower.includes(tok));

      if (isSpineLike || verticalOffset > 0 || isNeckHead) {
        if (!upwardCandidates.includes(sib)) upwardCandidates.push(sib);
      }
    }
  }

  if (upwardCandidates.length === 0) return [[], [], null, null];

  let spatialChestCandidates = new Set<number>();
  if (spatialData != null) {
    const pelvisVert = spatialData.getVerticalPosition(pelvis.index);
    spatialChestCandidates = new Set(findSpatialChestCandidates(skel, spatialData, pelvisVert));
  }

  function spineCandidateScore(c: number): number {
    const bone = skel.bone(c);
    const maxSubtree = Math.max(...upwardCandidates.map(cand => skel.bone(cand).subtreeSize));
    const subtreeScore = maxSubtree > 0 ? bone.subtreeSize / maxSubtree : 0.0;
    const pos = boneWorldPos(bone);
    const centerScore = centerAlignmentScore(pos, latAxis, pelvisPos, centerTolerance);
    return 0.7 * subtreeScore + 0.3 * centerScore;
  }

  const start = upwardCandidates.reduce((best, c) =>
    spineCandidateScore(c) > spineCandidateScore(best) ? c : best,
  );

  // Check if starting bone is already neck/head
  const startNameLower = skel.bone(start).name.toLowerCase();
  if (isSimpleHeadSegmentName(startNameLower)) {
    return [[], [], start, null];
  }
  if (neckTerms.some(t => startNameLower.includes(t))) {
    let spineChain: number[] = [];
    let neckChain: number[] = [];
    let headIdx: number | null = null;
    let chestIdx: number | null = null;

    let current: number | null = start;
    while (current != null) {
      const bone = skel.bone(current);
      const nameLower = bone.name.toLowerCase();

      const curHasHead = headTerms.some(t => nameLower.includes(t));
      const curHasNeck = neckTerms.some(t => nameLower.includes(t));
      const isHead = curHasHead && !curHasNeck;

      if (isHead) {
        headIdx = current;
        break;
      } else {
        neckChain.push(current);
      }

      let nonAuxChildren = getTraceableChildren(skel, bone.index, {
        includeAuxiliary: (child) => {
          const name = child.name.toLowerCase();
          return neckTerms.some(t => name.includes(t)) || headTerms.some(t => name.includes(t));
        },
        skip: (child) => isTorsoAuxiliaryLeafName(child.name),
      });
      const armSkip = [...(ROLE_TERMS['Clavicle'] ?? []), ...(ROLE_TERMS['UpperArm'] ?? [])];
      nonAuxChildren = nonAuxChildren.filter(
        c => !armSkip.some(tok => skel.bone(c).name.toLowerCase().includes(tok)),
      );

      if (nonAuxChildren.length === 1) {
        current = nonAuxChildren[0];
      } else if (nonAuxChildren.length === 0) {
        if (headIdx == null) {
          headIdx = current;
          if (neckChain.length > 0) neckChain.pop();
        }
        break;
      } else {
        if (curHasNeck && !curHasHead) {
          for (const child of nonAuxChildren) {
            const childName = skel.bone(child).name.toLowerCase();
            const chH = headTerms.some(t => childName.includes(t));
            const chN = neckTerms.some(t => childName.includes(t));
            if (chH && !chN) { headIdx = child; break; }
          }
          if (headIdx == null) {
            headIdx = current;
            if (neckChain.length > 0) neckChain.pop();
          }
          break;
        } else if (curHasNeck && curHasHead) {
          const childContinuesNeck = nonAuxChildren.some(
            c => neckTerms.some(t => skel.bone(c).name.toLowerCase().includes(t)),
          );
          if (!childContinuesNeck) {
            headIdx = current;
            if (neckChain.length > 0) neckChain.pop();
            break;
          }
          for (const c of nonAuxChildren) {
            if (neckTerms.some(t => skel.bone(c).name.toLowerCase().includes(t))) {
              current = c;
              break;
            }
          }
          continue;
        }
        for (const child of nonAuxChildren) {
          const childName = skel.bone(child).name.toLowerCase();
          const chH = headTerms.some(t => childName.includes(t));
          const chN = neckTerms.some(t => childName.includes(t));
          if (chH && !chN) { headIdx = child; break; }
        }
        if (headIdx == null) headIdx = nonAuxChildren[0];
        break;
      }
    }

    // Direction guard for neck
    if (spatialData != null && neckChain.length > 0) {
      const up = spatialData.upAxis;
      let pruned = false;
      for (let i = 0; i < neckChain.length; i++) {
        const bdir = skel.bone(neckChain[i]).direction;
        const dlen = bdir.length();
        if (dlen > 1e-8) {
          const align = bdir.clone().divideScalar(dlen).dot(up);
          if (align < -0.3) {
            neckChain = neckChain.slice(0, i);
            pruned = true;
            break;
          }
        }
      }

      if (pruned) {
        headIdx = null;
        if (neckChain.length === 1) {
          headIdx = neckChain[0];
          neckChain = [];
        } else if (neckChain.length === 0) {
          headIdx = start;
        } else if (headIdx == null) {
          headIdx = neckChain[neckChain.length - 1];
          neckChain = neckChain.slice(0, -1);
        }
      }
    }

    // Validate head direction (Guard 3)
    if (spatialData != null && headIdx != null) {
      const up = spatialData.upAxis;
      const hdir = skel.bone(headIdx).direction;
      const hlen = hdir.length();
      if (hlen > 1e-8) {
        const halign = hdir.clone().divideScalar(hlen).dot(up);
        if (halign < -0.5) {
          if (neckChain.length > 0) {
            headIdx = neckChain[neckChain.length - 1];
            neckChain = neckChain.slice(0, -1);
          }
        }
      }
    }

    return [spineChain, neckChain, headIdx, chestIdx];
  }

  // Follow spine chain until branching point
  const legBoneIndices = new Set<number>();
  if (leftChain) for (const idx of leftChain) legBoneIndices.add(idx);
  if (rightChain) for (const idx of rightChain) legBoneIndices.add(idx);

  const spineChain: number[] = [];
  let current: number | null = start;

  while (current != null) {
    const nameLower = skel.bone(current).name.toLowerCase();
    if (neckTerms.some(t => nameLower.includes(t)) || headTerms.some(t => nameLower.includes(t))) {
      break;
    }
    spineChain.push(current);
    const bone = skel.bone(current);

    let nonAuxChildren = getTraceableChildren(skel, bone.index, {
      skip: (child) => isTorsoAuxiliaryLeafName(child.name),
    }).filter(
      c => !legBoneIndices.has(c),
    );

    const functionalChildren: number[] = [];
    for (const c of nonAuxChildren) {
      const childName = skel.bone(c).name.toLowerCase();
      if ((ROLE_TERMS['Pelvis'] ?? []).some(tok => childName.includes(tok))) {
        continue;
      }
      if (['bow', 'weapon', 'sword', 'gun', 'shield', 'wing', 'tail'].some(tok => childName.includes(tok))) {
        continue;
      }
      functionalChildren.push(c);
    }
    nonAuxChildren = functionalChildren;

    if (nonAuxChildren.length === 0) {
      break;
    } else if (nonAuxChildren.length === 1) {
      current = nonAuxChildren[0];
    } else {
      const spineChildren = nonAuxChildren.filter(
        (c) => isSpineLikeName(skel.bone(c).name),
      );
      const viableSpineChildren = spineChildren.filter((c) => {
        const child = skel.bone(c);
        return !isTorsoAuxiliaryLeafName(child.name) && child.children.length > 0;
      });
      const structuralTorsoChildren = nonAuxChildren
        .filter((c) => !spineChildren.includes(c))
        .filter((c) => torsoContinuationScore(skel, c, latAxis, pelvisPos, centerTolerance) > 2.0)
        .sort(
          (a, b) =>
            torsoContinuationScore(skel, b, latAxis, pelvisPos, centerTolerance) -
            torsoContinuationScore(skel, a, latAxis, pelvisPos, centerTolerance),
        );
      const centerChildren: number[] = [];
      for (const c of nonAuxChildren) {
        if (spineChildren.includes(c)) continue;
        if (structuralTorsoChildren.includes(c)) continue;
        const childPos = boneWorldPos(skel.bone(c));
        const cs = centerAlignmentScore(childPos, latAxis, pelvisPos, centerTolerance);
        if (cs > 0.8) centerChildren.push(c);
      }

      if (viableSpineChildren.length === 1) {
        current = viableSpineChildren[0];
      } else if (viableSpineChildren.length > 1) {
        viableSpineChildren.sort(
          (a, b) =>
            torsoContinuationScore(skel, b, latAxis, pelvisPos, centerTolerance) -
            torsoContinuationScore(skel, a, latAxis, pelvisPos, centerTolerance),
        );
        current = viableSpineChildren[0];
      } else if (spineChildren.length === 1) {
        current = spineChildren[0];
      } else if (structuralTorsoChildren.length === 1) {
        current = structuralTorsoChildren[0];
      } else if (spineChildren.length === 0 && centerChildren.length === 1) {
        current = centerChildren[0];
      } else {
        break;
      }
    }
  }

  let neckChain: number[] = [];
  let headIdx: number | null = null;
  let chestIdx = spineChain.length > 0 ? spineChain[spineChain.length - 1] : null;

  if (spineChain.length === 0) return [[], [], null, null];

  if (spineChain.length > 0) {
    const chestBone = skel.bone(spineChain[spineChain.length - 1]);
    const chestChildren = getTraceableChildren(skel, chestBone.index, {
      skip: (child) => isTorsoAuxiliaryLeafName(child.name),
    });

    let neckStart: number | null = null;
    for (const child of chestChildren) {
      const childBone = skel.bone(child);
      const nameLower = childBone.name.toLowerCase();
      if (neckTerms.some(t => nameLower.includes(t))) {
        neckStart = child;
        break;
      }
    }

    if (neckStart == null) {
      const clavTerms = ROLE_TERMS['Clavicle'] ?? [];
      for (const child of chestChildren) {
        const childBone = skel.bone(child);
        if (childBone.hasSide) continue;
        const nameLower = childBone.name.toLowerCase();
        if (clavTerms.some(p => nameLower.includes(p))) continue;
        if (['hatch', 'hook', 'mount', 'attach', 'fx', 'ik'].some(p => nameLower.includes(p))) continue;
        neckStart = child;
        break;
      }
    }

    if (neckStart != null) {
      let cur: number | null = neckStart;
      while (cur != null) {
        const bone = skel.bone(cur);
        const nameLower = bone.name.toLowerCase();

        const curHasHead = headTerms.some(t => nameLower.includes(t));
        const curHasNeck = neckTerms.some(t => nameLower.includes(t));

        if (curHasHead && !curHasNeck) {
          headIdx = cur;
          break;
        } else {
          neckChain.push(cur);
        }

        const nonAuxKids = getTraceableChildren(skel, bone.index, {
          skip: (child) => isTorsoAuxiliaryLeafName(child.name),
        });
        if (nonAuxKids.length === 1) {
          cur = nonAuxKids[0];
        } else if (nonAuxKids.length === 0) {
          if (headIdx == null && !curHasNeck) {
            headIdx = cur;
            neckChain.pop();
          }
          break;
        } else {
          if (curHasNeck && !curHasHead) {
            for (const child of nonAuxKids) {
              const childName = skel.bone(child).name.toLowerCase();
              const chHasHead = headTerms.some(t => childName.includes(t));
              const chHasNeck = neckTerms.some(t => childName.includes(t));
              if (chHasHead && !chHasNeck) { headIdx = child; break; }
            }
            if (headIdx == null) {
              headIdx = cur;
              neckChain.pop();
            }
            break;
          } else if (curHasNeck && curHasHead) {
            const childContinuesNeck = nonAuxKids.some(
              c => neckTerms.some(t => skel.bone(c).name.toLowerCase().includes(t)),
            );
            if (!childContinuesNeck) {
              headIdx = cur;
              neckChain.pop();
              break;
            }
            for (const c of nonAuxKids) {
              if (neckTerms.some(t => skel.bone(c).name.toLowerCase().includes(t))) {
                cur = c;
                break;
              }
            }
            continue;
          }
          for (const child of nonAuxKids) {
            const childName = skel.bone(child).name.toLowerCase();
            const chHasHead = headTerms.some(t => childName.includes(t));
            const chHasNeck = neckTerms.some(t => childName.includes(t));
            if (chHasHead && !chHasNeck) { headIdx = child; break; }
          }
          if (headIdx == null) headIdx = nonAuxKids[0];
          break;
        }
      }
    }
  }

  // Drop very short inline first spine bone, unless it is the torso bridge above pelvis.
  if (spineChain.length >= 2) {
    const firstIdx = spineChain[0];
    const secondIdx = spineChain[1];
    const firstLen = skel.bone(firstIdx).length;
    const secondLen = skel.bone(secondIdx).length;
      const firstName = skel.bone(firstIdx).name.toLowerCase();
    const v1 = boneWorldPos(skel.bone(secondIdx)).clone().sub(boneWorldPos(skel.bone(firstIdx)));
    const n = v1.length();
    if (n > 1e-8) {
      v1.divideScalar(n);
      const firstIsNamedPelvisBridge =
        skel.bone(pelvis.index).parent === firstIdx && isPrimarySpineBridgeName(firstName);
      if (
        !firstIsNamedPelvisBridge &&
        firstLen < 0.5 * secondLen &&
        Math.abs(v1.dot(effUpAxis)) > 0.9
      ) {
        spineChain.splice(0, 1);
      }
    }
  }

  // Keep only torso-named bones.
  const spineFilterToks = ROLE_TERMS['Spine'] ?? [];
  let spineOnly = spineChain.filter(
    idx => {
      const name = skel.bone(idx).name.toLowerCase();
      const isSpine = spineFilterToks.some(tok => name.includes(tok));
      const isInlineChest = isInlineChestLikeName(name);
      return isSpine || isInlineChest;
    },
  );

  if (spineOnly.length > 3 && spineChain.length > 0) {
    const lastChestBone = skel.bone(spineChain[spineChain.length - 1]);
    const clavCheckToks = ROLE_TERMS['Clavicle'] ?? [];
    const hasClavicleChild = lastChestBone.children.some(c => {
      if (skel.bone(c).auxiliary) return false;
      return clavCheckToks.some(tok => skel.bone(c).name.toLowerCase().includes(tok));
    });

    if (hasClavicleChild) {
      const neckChildren = lastChestBone.children.filter(c => {
        if (skel.bone(c).auxiliary) return false;
        return neckTerms.some(t => skel.bone(c).name.toLowerCase().includes(t));
      });

      let neckLen = 0;
      if (neckChildren.length > 0) {
        let cur: number | null = neckChildren[0];
        while (cur != null) {
          neckLen++;
        const kids: number[] = skel.bone(cur).children.filter(c => {
          const child = skel.bone(c);
          return !child.auxiliary && !isTorsoAuxiliaryLeafName(child.name);
        });
          cur = kids.length === 1 ? kids[0] : null;
        }
      }

      if (neckLen <= 1) {
        spineOnly = spineOnly.slice(0, 3);
      } else {
        const lengths = spineOnly.map(i => skel.bone(i).length);
        if (lengths.length > 0) {
          const sorted = [...lengths].sort((a, b) => a - b);
          const medianLen = sorted[Math.floor(sorted.length / 2)];
          const tailShort = lengths.slice(-2).every(l => l < 0.5 * medianLen);
          if (tailShort) spineOnly = spineOnly.slice(0, -1);
        }
      }
    }
  }

  if (neckChain.length === 0 && headIdx != null && spineOnly.length >= 2) {
    const terminalSpineIdx = spineOnly[spineOnly.length - 1];
    const terminalSpineChildren = getTraceableChildren(skel, terminalSpineIdx, {
      skip: (child) => isTorsoAuxiliaryLeafName(child.name),
    });
    const terminalSpineChildrenOnlyHead =
      terminalSpineChildren.length > 0 &&
      terminalSpineChildren.every((childIdx) => {
        if (childIdx === headIdx) return true;
        const childName = skel.bone(childIdx).name.toLowerCase();
        return headTerms.some((term) => childName.includes(term));
      });

    const parentIdx = skel.bone(terminalSpineIdx).parent;
    const parentHasShoulderLikeSibling =
      parentIdx !== null &&
      getTraceableChildren(skel, parentIdx, {
        skip: (child) => isTorsoAuxiliaryLeafName(child.name),
      })
        .filter((childIdx) => childIdx !== terminalSpineIdx)
        .some((childIdx) => {
          const child = skel.bone(childIdx);
          const childName = child.name.toLowerCase();
          return (
            child.hasSide ||
            [...(ROLE_TERMS['Clavicle'] ?? []), ...(ROLE_TERMS['UpperArm'] ?? [])]
              .some((term) => childName.includes(term))
          );
        });

    if (terminalSpineChildrenOnlyHead && parentHasShoulderLikeSibling) {
      spineOnly = spineOnly.slice(0, -1);
      neckChain = [terminalSpineIdx];
      chestIdx = spineOnly.length > 0 ? spineOnly[spineOnly.length - 1] : null;
    }
  }

  if (headIdx == null && neckChain.length === 0 && spineOnly.length === 1) {
    const loneSpineIdx = spineOnly[0];
    const loneName = skel.bone(loneSpineIdx).name.toLowerCase();
    const hasHeadNaming =
      isSimpleHeadSegmentName(loneName) ||
      headTerms.some((term) => loneName.includes(term));
    const hasTorsoNaming =
      (ROLE_TERMS['Spine'] ?? []).some((term) => loneName.includes(term)) ||
      (ROLE_TERMS['Chest'] ?? []).some((term) => loneName.includes(term)) ||
      isInlineChestLikeName(loneName);

    if (hasHeadNaming && !hasTorsoNaming) {
      headIdx = loneSpineIdx;
      spineOnly = [];
      chestIdx = null;
    }
  }

  return [spineOnly, neckChain, headIdx, chestIdx];
}
